import * as React from 'react';
import { useNavigate } from 'react-router';
import type { PiletApi } from '@chess/platform-web';
import { libraryLink, type FilterLink } from '@chess/api-insights/charts';
import { registerAggregator, useAggregate } from '@chess/api-model/react';
import { HELP_API, type Help } from '@chess/api-help';
import { registerCard } from '@chess/api-dashboard';
import '@chess/api-insights/charts.css';
import { MIDDLEGAME_POSITIONS_CARD, MiddlegamePositionsCard } from './MiddlegamePositionsCard.tsx';
import { MiddlegamePositionsAggregator } from './MiddlegamePositionsAggregator.ts';
import { POSITIONS_HELP, positionsHelp } from './help.tsx';

// Middlegame card "Positions you misplay": registers its own aggregator with
// the game store, reads its result and renders `MiddlegamePositionsCard` on
// the Middlegame screen's second row (`middlegame.why`, order 20; the
// screen's own slot, not Home's). It registers the help topic explaining
// each kind of position; a bar opens the library filtered to its games
// (`libraryLink`, `mg-position`).
export function setup(app: PiletApi) {
  const getApi = <T,>(name: string) => app.getApi<T>(name);
  const aggregator = new MiddlegamePositionsAggregator();
  registerAggregator(getApi, aggregator);
  void app.getApi<Help>(HELP_API).then((help) => help.register(POSITIONS_HELP, positionsHelp));
  const Card: React.FC = () => {
    const { result, state } = useAggregate(getApi, aggregator);
    const navigate = useNavigate();
    const link = React.useMemo<FilterLink>(() => libraryLink((path) => navigate(path)), [navigate]);
    // Never hidden while the store loads: the card shows a skeleton of its bars.
    if (state?.status !== 'ready') return state?.status === 'failed' ? null : <MiddlegamePositionsCard loading />;
    return result ? <MiddlegamePositionsCard result={result} link={link} /> : null;
  };
  registerCard(app, 'middlegame.why', Card, { order: 20, ...MIDDLEGAME_POSITIONS_CARD });
}
